import AxiosService from '@core/services/app/axiosService';
import authHeader from '@core/services/app/authHeader';
import appNotificationService from '@core/services/utils/appNotificationService';
import appConsoleService from '@core/services/utils/appConsoleService';
import EventBus from '@core/services/utils/appEventBusService';
import BusEnum from '@core/enums/BusEnum';
import {useAppInboxStore} from '@core/stores/app/appInboxStore';

/**
 * AppInboxService — centre de notifications (inbox persistée).
 * Charge la liste, gère lu/non-lu, et écoute le canal temps-réel WebSocket
 * (NOTIFICATION_NEW) pour mettre à jour le badge + afficher un toast. Singleton.
 */
class AppInboxService {

  getStore() {
    if (!this.store) {
      this.store = useAppInboxStore()
    }
    return this.store
  }

  /** Enregistre l'écoute temps-réel (une seule fois). Appelé au boot. */
  init() {
    if (this._inited) return
    this._inited = true
    EventBus.on(BusEnum.NOTIFICATION_NEW, (payload) => {
      // Backend enveloppe en {data, timestamp} ; on accepte aussi le DTO brut.
      const notif = (payload && payload.data) ? payload.data : payload
      if (!notif) return
      this.getStore().prepend(notif)
      // Le son (« Cristal », synthétisé) est joué par appNotificationService ci-dessous.
      // Message d'un utilisateur : toast avec l'avatar et le nom de l'expéditeur ; sinon information simple.
      if (notif.sender) appNotificationService.pushMessage({sender: notif.sender, title: notif.title, body: notif.body})
      else appNotificationService.pushInfo(notif.title + (notif.body ? ' — ' + notif.body : ''))
    })
  }

  async load(max = 30, offset = 0) {
    try {
      const response = await AxiosService.post('/appNotification/list', {max, offset}, {headers: authHeader()});
      this.getStore().load(response.data);
      return response.data;
    } catch (error) {
      appConsoleService.error('appInboxService', error);
    }
  }

  async markRead(id) {
    this.getStore().markRead(id);
    try {
      await AxiosService.post('/appNotification/markRead', {id}, {headers: authHeader()});
    } catch (error) {
      appConsoleService.error('appInboxService', error);
    }
  }

  async markAllRead() {
    this.getStore().markAllRead();
    try {
      await AxiosService.post('/appNotification/markAllRead', {}, {headers: authHeader()});
    } catch (error) {
      appConsoleService.error('appInboxService', error);
    }
  }

  async remove(id) {
    this.getStore().remove(id);
    try {
      await AxiosService.post('/appNotification/remove', {id}, {headers: authHeader()});
    } catch (error) {
      appConsoleService.error('appInboxService', error);
    }
  }

  /** Destinataires possibles d'un message : [{id, name, username, image}]. */
  async recipients() {
    try {
      const response = await AxiosService.post('/appNotification/recipients', {}, {headers: authHeader()});
      return response.data.recipients || [];
    } catch (error) {
      appConsoleService.error('appInboxService.recipients', error);
      return [];
    }
  }

  /**
   * Message libre à un autre utilisateur. Toast de retour selon la livraison :
   * en ligne / appareils abonnés au push / seulement dans sa cloche.
   * @returns {boolean} envoyé
   */
  async sendMessage(userId, body, recipientName = '') {
    try {
      const response = await AxiosService.post('/appNotification/sendMessage', {userId, body}, {headers: authHeader()});
      const r = response.data || {};
      const who = r.recipient || recipientName;
      const how = r.online ? 'en ligne'
        : r.devices ? `${r.devices} appareil${r.devices > 1 ? 's' : ''} notifié${r.devices > 1 ? 's' : ''}`
          : 'le message l\'attend dans sa cloche';
      appNotificationService.pushInfo(`Message envoyé à ${who} (${how}).`);
      return true;
    } catch (error) {
      appNotificationService.pushError(error?.response?.data?.message || error);
      return false;
    }
  }

  /** Admin → envoie un message (notification) à un utilisateur. */
  async adminSendToUser(userId, title, body) {
    try {
      await AxiosService.post('/appNotification/sendToUser', {userId, title, body}, {headers: authHeader()});
      return true;
    } catch (error) {
      appNotificationService.pushError(error);
      return false;
    }
  }
}

export default new AppInboxService();
